const selector_trigger = '.accordion__heading';
const selector_target = '.accordion__content';
const open_marker = 'accordion--open';

let accordionNumber = 0;

function toggleItem(accordion: Element, controlledElement: HTMLElement, open: boolean): void {
	if (open) { // toggle returns true if we are opening the item
		controlledElement.style.display = '';
		controlledElement.style.maxHeight = `${controlledElement.scrollHeight}px`;

		// Set the fragment to the id of the accordion we are opening
		const url = new URL(window.location.href);
		url.hash = accordion.id;
		history.replaceState(history.state, "", url);
	} else {
		controlledElement.style.maxHeight = "0px";

		// If the current fragment is the id of the accordion we are closing, remove it from the URL
		if (window.location.hash && window.location.hash.substring(1) === accordion.id) {
			const url = new URL(window.location.href);
			url.hash = document.querySelector(`.accordion.${open_marker}`)?.id ?? "";
			history.replaceState(history.state, "", url);
		}
	}
}

function setupIconAndButton(accordionTrigger: HTMLElement, accordionContentId: string) {
	let button: HTMLButtonElement|null;
	if (!(button = accordionTrigger.querySelector<HTMLButtonElement>("button"))) {
		button = document.createElement("button");
		button.innerHTML = `
			<svg viewBox="0 0 22 22" role="img" aria-hidden="true" width="22" height="22">
				<path class="path-V" d="M8.5 0h5v22h-5z"/>
				<path class="path-H" d="M0 8.5h22v5H0z"/>
			</svg>
		`;

		button.setAttribute("aria-expanded", "false");
		button.setAttribute("aria-controls", accordionContentId);
		for (let node: typeof accordionTrigger.firstChild; (node = accordionTrigger.firstChild) !== null;) {
			button.appendChild(node);
		}
		accordionTrigger.appendChild(button);
	}
	return button;
}

function wireUpAccordionGroup(accordionGroup: HTMLElement, observe: (element: Element) => void) {
	if (accordionGroup.classList.contains("wp-block-plaudit-accordion-group--js-ready")) {
		return;
	}
	accordionGroup.classList.add("wp-block-plaudit-accordion-group--js-ready");

	accordionGroup.addEventListener('click', event => {
		let button: HTMLButtonElement|null;
		if (event.target instanceof Element && (button = event.target.closest("button"))) {
			const heading = event.target.closest(selector_trigger);
			if (!heading) {
				return;
			}

			const accordion = heading.closest(".wp-block-plaudit-accordion");
			if (accordion) {
				event.preventDefault();
				event.stopPropagation();

				const open = accordion.classList.toggle(open_marker);
				button.setAttribute("aria-expanded", open.toString());

				const controlledElementId = button.getAttribute("aria-controls");
				if (!controlledElementId) {
					return;
				}
				const controlledElement = document.getElementById(controlledElementId);
				if (controlledElement) {
					controlledElement.setAttribute("data-plaudit-accordion-transition-count",
						(parseInt(controlledElement.getAttribute("data-plaudit-accordion-transition-count") || "0") + 1).toString());
					toggleItem(accordion, controlledElement, open);
				}
			}
		}
	});

	const frag = window.location.hash ? window.location.hash.substring(1) : null;
	for (const accordion of accordionGroup.querySelectorAll(".wp-block-plaudit-accordion")) {
		const accordionContent = accordion.querySelector<HTMLElement>(selector_target);
		const accordionTrigger = accordion.querySelector<HTMLElement>(selector_trigger);
		if (accordionContent && accordionTrigger) {
			accordionContent.setAttribute("data-plaudit-accordion-transition-count", "0");
			observe(accordionContent);
			let accordionId: string|undefined;
			if (!(accordionId = accordion.id).length) {
				accordionId = accordionTrigger.innerText.trim()
					.replace(/([a-z])([A-Z])/g, "$1-$2")
					.replace(/[\s_]+/g, '-')
					.toLowerCase();

				if (!accordionId) {
					accordionId = `p-accordion-${++accordionNumber}`;
				} else if (document.getElementById(accordionId)) { // We use else-if because we only need to check for overlap when we're using an id based on the trigger's contents
					// If the generated id would overlap with the id of another element on the page, we append a suffix to ensure that it remains unique
					accordionId += "-" + (++accordionNumber);
				}
				accordion.id = accordionId;
			}
			if (!accordionContent.id) {
				accordionContent.id = accordionId + "-content";
			}

			const button = setupIconAndButton(accordionTrigger, accordionContent.id);

			const onEnd = (evt: TransitionEvent) => {
				if (evt.propertyName === 'max-height' && evt.target === accordionContent) {
					if (accordionContent.style.maxHeight === "0px") {
						accordionContent.style.display = 'none';
					}
					accordionContent.setAttribute("data-plaudit-accordion-transition-count",
						(parseInt(accordionContent.getAttribute("data-plaudit-accordion-transition-count") || "0") - 1).toString());
				}
			}
			accordionContent.addEventListener('transitionend', onEnd);
			accordionContent.addEventListener('transitioncancel', onEnd);

			if (accordionId === frag) {
				accordion.classList.toggle(open_marker, true);
				button.setAttribute("aria-expanded", "true");
				accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
			} else {
				accordionContent.style.maxHeight = "0px"; // We have to set both because we check the max-height property when determining whether we should resize something
				accordionContent.style.display = "none"; // This is the equivalent of using JQuery's slideUp function with a duration of 0
			}
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const resizeObserver = new ResizeObserver(entries => {
		for (const entry of entries) {
			const element = entry.target;
			if (
				element instanceof HTMLElement && element.getAttribute("data-plaudit-accordion-transition-count") === "0"
				&& element.style.maxHeight && element.style.maxHeight !== "0px"
			) {
				element.style.maxHeight = ""; // We have to unset the maxHeight before recalculating the height, otherwise it can never increase
				element.style.maxHeight = `${entry.target.scrollHeight}px`;
			}
		}
	});
	const observe = (element: Element) => resizeObserver.observe(element);

	// NOTE: accordion can only be a child of accordionGroup
	for (const e of document.querySelectorAll<HTMLElement>('.wp-block-plaudit-accordion-group')) {
		wireUpAccordionGroup(e, observe);
	}
	new MutationObserver(mutations => {
		for (const mutation of mutations) {
			for (const addedNode of mutation.addedNodes) {
				if (addedNode instanceof HTMLElement) {
					if (addedNode.classList.contains("wp-block-plaudit-accordion-group")) {
						wireUpAccordionGroup(addedNode, observe);
					}
					for (const e of addedNode.querySelectorAll<HTMLElement>('.wp-block-plaudit-accordion-group')) {
						wireUpAccordionGroup(e, observe);
					}
				}
			}
		}
	}).observe(document.body, {subtree: true, childList: true});
});
